import { FC } from 'react';
import { Link } from '@chakra-ui/react';

import { InvoiceType } from '__generated__/GQL';
import getErrorMessageIfPossible from 'common/hooks/useErrorHandledMutation/getErrorMessageIfPossible';
import useToast from 'components/ToastProvider/useToast';
import { ContactOptions } from 'features/Work/components/ContactPicker';
import FiscalIdModal from 'features/Work/components/FiscalIdModal';
import useLastInvoiceFiscalId from 'features/Work/hooks/useLastInvoiceFiscalId';
import { useInvoiceModals } from 'features/Work/pages/Invoices/hooks/useInvoiceModals';

import { INVOICE_NUMBER_ARTICLE } from '../../../../../../constants';
import useSendInvoice from '../../../mutations/useSendInvoice';
import locales from './locales';

export interface SendModalProps {
  invoiceId: string;
  contactOptions: ContactOptions;
}

const SendModal: FC<SendModalProps> = ({
  contactOptions: { clientContactIds, isUserCcedOnEmails },
  invoiceId,
}) => {
  const { showToast } = useToast();
  const { hideModal } = useInvoiceModals();
  const { lastInvoiceFiscalId, loading } = useLastInvoiceFiscalId();
  const { onSendInvoice } = useSendInvoice();

  const onSubmit = async (fiscalId: string) => {
    try {
      await onSendInvoice({
        input: {
          clientContactIds,
          fiscalId,
          invoiceId,
          isUserCcedOnEmails,
        },
      });

      showToast({
        message: locales.successMessage,
        type: 'success',
      });

      hideModal();
    } catch (error) {
      showToast({
        message: getErrorMessageIfPossible({ error }) || locales.errorMessage,
        type: 'error',
      });
    }
  };

  return (
    <FiscalIdModal onClose={hideModal}>
      <FiscalIdModal.Body>
        <FiscalIdModal.Body.Title>{locales.title}</FiscalIdModal.Body.Title>

        <FiscalIdModal.Body.Description>
          {locales.description.start}
          <Link
            color="blue.600"
            fontWeight="weight-400"
            href={INVOICE_NUMBER_ARTICLE}
            target="_blank"
          >
            {locales.description.link}
          </Link>
          {locales.description.end}
        </FiscalIdModal.Body.Description>

        <FiscalIdModal.Body.Input
          fiscalId={lastInvoiceFiscalId?.next ?? ''}
          lastFiscalId={lastInvoiceFiscalId?.last ?? undefined}
          loading={loading}
          type={InvoiceType.Invoice}
        />
      </FiscalIdModal.Body>

      <FiscalIdModal.Footer>
        <FiscalIdModal.Footer.CancelButton onClick={hideModal}>
          {locales.cancel}
        </FiscalIdModal.Footer.CancelButton>

        <FiscalIdModal.Footer.SubmitButton onClick={onSubmit}>
          {locales.submit}
        </FiscalIdModal.Footer.SubmitButton>
      </FiscalIdModal.Footer>
    </FiscalIdModal>
  );
};

export default SendModal;
